/* 2026-10-21, Arturo: "necesito agregarle documentos de soporte para
   cuando haya ajustes en el precio y que esos documentos se anexen al
   reporte pdf dentro del mismo no como archivo separado... se gerene
   el pdf en la primer hoja y la siguientes sean de documentos anexos
   de evidencias pueden ser varios archivos o un pdf con mas de una
   hoja" — pure PDF-page-merge helper, no UI, used by SalesReports.js's
   buildSalesReportBlob so the generated Sales Report and the
   evidence the user attached to the Bill always come back as ONE
   PDF (report page(s) first, evidence pages appended after), rather
   than separate email attachments. Built on pdf-lib (already a repo
   dependency, see package.json).

   `supportingDocs` is a plain array of `{ name, type, url }` — the
   Supabase Storage metadata BillRegistry.js's SupportingDocsButton
   persists directly on the bill record (bucket "bill-supporting-docs",
   see migrations/2026-10-21_bill_supporting_docs_bucket.sql). 2026-10-21,
   Arturo: these used to live only in the browser's local IndexedDB —
   "no vuelvas a hacer eso... nunca se debe almacenar nada en modo
   local" — moved to Storage so the same files are available from any
   device, and this helper now fetches each file's bytes from its
   public URL instead of reading a local File/Blob. Each entry is
   either a PDF (its pages are copied in, in order — a multi-page PDF
   contributes all of its pages) or an image (embedded onto its own
   new Letter-size page, centered and scaled to fit). Any other file
   type is silently skipped — pdf-lib can't rasterize a .docx/.xlsx,
   and callers restrict uploads to pdf/jpg/png for this feature
   specifically (see ALLOWED_SUPPORTING_DOC_EXT below).

   This is a SEPARATE helper from the top-level src/pdfMerge.js
   (mergePdfBlobs, used by Organic Audit reports) — that one always
   builds a brand-new document from a flat list of PDF-only inputs; this
   one appends onto an existing base document and also handles images,
   neither of which the Organic Audit use case needs. Not consolidated,
   per this codebase's per-feature "duplicate rather than share"
   convention — but named identically to that other file, so don't
   confuse the two when editing.

   2026-10-24, Arturo (urgent): "cada reporte debe agregar sus anexos
   en seguida... lote 58049 su reporte y sus documentos de soporte
   inmediatamente despues, posterior lote con sus soportes... cuando
   selecciono mas de un lote, deben ir en orden" — SalesReports.js's
   buildSalesReportBlob used to call mergeDocsAtEnd (then named
   mergePdfWithSupportingDocs with ALL bills' docs concatenated) AFTER
   building the combined multi-bill report, so every bill's evidence
   landed at the very end instead of right after its own report page.
   First fix attempt built each bill's report+docs as a SEPARATE
   single-page jsPDF blob and tried to stitch N of those back together
   with the top-level pdfMerge.js's mergePdfBlobs — that broke
   entirely in production ("mergePdfBlobs: every input failed to
   parse"), a code path never exercised before (pdf-lib loading
   multiple independent jsPDF blobs it never touched). Reverted to a
   safer design using only ALREADY-PROVEN primitives: ONE jsPDF call
   builds the whole N-bill report (htmlDocsToPdfBlob, exactly as
   before — this always worked), then ONE pdf-lib pass
   (mergeSalesReportWithPerBillDocs below) walks that document page by
   page and, right after copying bill i's own report page into the
   output, immediately appends bill i's own supporting docs — same
   fetch-and-embed logic mergePdfWithSupportingDocs already used
   successfully in production, just invoked per-bill-position instead
   of once at the end. */

import { PDFDocument } from "pdf-lib";

export var ALLOWED_SUPPORTING_DOC_EXT = ["pdf", "jpg", "jpeg", "png"];

/* US Letter in PDF points (612x792) — matches the page size the rest
   of this app's PDFs render at (pdfRenderer.js). */
var LETTER_W = 612, LETTER_H = 792, MARGIN = 36;

/* Fetches and appends each supporting doc's pages/image onto an
   ALREADY-OPEN pdf-lib PDFDocument (`targetDoc`), in order. Shared by
   mergePdfWithSupportingDocs (appends once, at the end) and
   mergeSalesReportWithPerBillDocs (appends once per bill, right after
   that bill's own report page) so the fetch/embed logic exists in
   exactly one place. Never throws — a single unreadable doc is logged
   and skipped, same as before; the caller's own try/catch still
   covers a total failure (e.g. the base PDF itself being corrupt). */
async function appendSupportingDocsToDoc(targetDoc, supportingDocs) {
  var list = Array.isArray(supportingDocs) ? supportingDocs : [];
  for (var i = 0; i < list.length; i++) {
    var doc = list[i];
    if (!doc || !doc.url) continue;
    var name = doc.name || "";
    var type = (doc.type || "").toLowerCase();
    var isPdf = type === "application/pdf" || /\.pdf$/i.test(name);
    var isPng = type === "image/png" || /\.png$/i.test(name);
    var isJpg = type === "image/jpeg" || type === "image/jpg" || /\.(jpe?g)$/i.test(name);

    try {
      var resp = await fetch(doc.url);
      if (!resp.ok) throw new Error("fetch failed: " + resp.status);
      var bytes = new Uint8Array(await resp.arrayBuffer());
      if (isPdf) {
        var srcDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
        var pages = await targetDoc.copyPages(srcDoc, srcDoc.getPageIndices());
        pages.forEach(function (p) { targetDoc.addPage(p); });
      } else if (isPng || isJpg) {
        var img = isPng ? await targetDoc.embedPng(bytes) : await targetDoc.embedJpg(bytes);
        var maxW = LETTER_W - MARGIN * 2, maxH = LETTER_H - MARGIN * 2;
        var scale = Math.min(maxW / img.width, maxH / img.height, 1);
        var w = img.width * scale, h = img.height * scale;
        var page = targetDoc.addPage([LETTER_W, LETTER_H]);
        page.drawImage(img, { x: (LETTER_W - w) / 2, y: (LETTER_H - h) / 2, width: w, height: h });
      }
    } catch (inner) {
      console.error("[pdfMerge] skipping unreadable supporting doc:", doc.name, inner);
    }
  }
}

/**
 * Merge a base PDF Blob with supporting-document pages appended after it.
 * Never throws — on any failure it logs and returns the original base
 * blob unmerged, so a bad/corrupt evidence file never blocks the real
 * Sales Report from going out.
 * @param {Blob} baseBlob
 * @param {Array<{name:string,type:string,url:string}>} supportingDocs
 * @returns {Promise<Blob>}
 */
export async function mergePdfWithSupportingDocs(baseBlob, supportingDocs) {
  if (!Array.isArray(supportingDocs) || supportingDocs.length === 0) return baseBlob;
  try {
    var baseBytes = new Uint8Array(await baseBlob.arrayBuffer());
    var mergedDoc = await PDFDocument.load(baseBytes);
    await appendSupportingDocsToDoc(mergedDoc, supportingDocs);
    var mergedBytes = await mergedDoc.save();
    return new Blob([mergedBytes], { type: "application/pdf" });
  } catch (e) {
    console.error("[pdfMerge] merge failed, sending base PDF without supporting docs:", e);
    return baseBlob;
  }
}

/**
 * Sales Report, multi-bill case: `baseBlob` is the already-built
 * combined report (one page per bill, in bill order — htmlDocsToPdfBlob's
 * contract). Walks it page by page and, right after copying bill i's
 * own report page into the output, appends bill i's own
 * `extra.priceAdjustmentDocs` — so each lot's evidence lands
 * immediately after that lot's own report page, not all bunched at
 * the very end. `bills` must be the SAME array (same order) used to
 * build `baseBlob`. Never throws — falls back to the base blob
 * unmerged on any failure, same defensive contract as
 * mergePdfWithSupportingDocs above.
 * @param {Blob} baseBlob
 * @param {Array<Object>} bills
 * @returns {Promise<Blob>}
 */
export async function mergeSalesReportWithPerBillDocs(baseBlob, bills) {
  var hasAnyDocs = (bills || []).some(function (b) { return b && b.extra && Array.isArray(b.extra.priceAdjustmentDocs) && b.extra.priceAdjustmentDocs.length > 0; });
  if (!hasAnyDocs) return baseBlob;
  try {
    var baseBytes = new Uint8Array(await baseBlob.arrayBuffer());
    var srcDoc = await PDFDocument.load(baseBytes);
    var outDoc = await PDFDocument.create();
    for (var i = 0; i < bills.length; i++) {
      var copied = await outDoc.copyPages(srcDoc, [i]);
      copied.forEach(function (p) { outDoc.addPage(p); });
      var billDocs = bills[i] && bills[i].extra && bills[i].extra.priceAdjustmentDocs;
      await appendSupportingDocsToDoc(outDoc, billDocs);
    }
    var outBytes = await outDoc.save();
    return new Blob([outBytes], { type: "application/pdf" });
  } catch (e) {
    console.error("[pdfMerge] per-bill interleave failed, sending report without supporting docs reordered:", e);
    return baseBlob;
  }
}
